import { useState, useEffect, useRef } from "react";
import { Textarea } from "@/modern/components/ui/textarea";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { useDebounce } from "@/react/hooks/useDebounce";
import { useFileUpdate } from "@/react/components/entityViews/files/api/files.api";
import { cn } from "@/modern/utils";
import { FILE_DETAIL_FIELD_CONTROL_CLS, FILE_DETAIL_FIELD_LABEL_CLS } from "./fileDetailFormClasses";

interface FileDescriptionSectionProps {
  canEditFile?: boolean;
  /** @deprecated Unused — grants come from canEditFile / context. */
  isCCFile?: boolean;
}

function normalizeDescription(value: string | null | undefined): string | null {
  return value?.trim() || null;
}

/**
 * Debounced description editor for the current file.
 *
 * Parent should pass `key={file.id}` so this remounts on navigation. That
 * prevents the previous file's draft/debounce from being saved onto the next
 * file (which caused a null/text PATCH loop).
 */
export const FileDescriptionSection = ({ canEditFile: canEditFileProp }: FileDescriptionSectionProps = {}) => {
  const { canEditFile: canEditFileFromContext, file } = useFileDetailModal();
  const { mutate: updateFile } = useFileUpdate();

  const canEditFile = canEditFileProp ?? canEditFileFromContext;
  const isEditable = !!canEditFile;
  const fileId = file?.id;
  const serverDescription = file?.description ?? "";

  const [localDescription, setLocalDescription] = useState(serverDescription);
  const debouncedDescription = useDebounce(localDescription, 500);

  const localDescriptionRef = useRef(localDescription);
  const lastSubmittedRef = useRef<string | null>(normalizeDescription(serverDescription));
  const isEditableRef = useRef(isEditable);
  const updateFileRef = useRef(updateFile);

  localDescriptionRef.current = localDescription;
  isEditableRef.current = isEditable;
  updateFileRef.current = updateFile;

  const persistDescription = (targetFileId: number, value: string) => {
    if (!isEditableRef.current) return;

    const trimmedDescription = normalizeDescription(value);
    if (trimmedDescription === lastSubmittedRef.current) return;

    lastSubmittedRef.current = trimmedDescription;
    updateFileRef.current({ fileId: targetFileId, payload: { description: trimmedDescription } });
  };

  // Persist after debounce settles (same file only — remount resets state on nav).
  useEffect(() => {
    if (!isEditable || fileId == null) return;
    if (localDescription !== debouncedDescription) return;

    persistDescription(fileId, debouncedDescription);
  }, [debouncedDescription, localDescription, fileId, isEditable]);

  // Flush unsaved draft on unmount (arrow nav remount or modal close).
  useEffect(() => {
    if (fileId == null) return;

    return () => {
      persistDescription(fileId, localDescriptionRef.current);
    };
  }, [fileId]);

  return (
    <div>
      <label htmlFor="description" className={FILE_DETAIL_FIELD_LABEL_CLS}>
        Description
      </label>
      <Textarea
        id="description"
        value={localDescription}
        onChange={(event) => setLocalDescription(event.target.value)}
        placeholder="Add helpful context for this file"
        readOnly={!isEditable}
        rows={3}
        className={cn(
          FILE_DETAIL_FIELD_CONTROL_CLS,
          "min-h-[4.5rem] resize-y focus-visible:ring-inset focus-visible:ring-offset-0",
        )}
      />
    </div>
  );
};
